import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, Card } from 'components/atoms';
import { FormInput, LabeledStatus, ViewToggleButton } from 'components/molecules';
import ListWrapper from 'components/molecules/list-wrapper/ListWrapper';
import { QueryFilter, TopBar } from 'components/organisms';
import QueryFilterLabels from 'components/organisms/filters/query-form/labels/QueryFilterLabels';
import { TopbarPage } from 'components/templates';
import { useScroll } from 'components/templates/application/ApplicationTemplate';
import { useComponentVisible } from 'hooks';
import { useListViewById } from 'hooks/useListViewById';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { PiList, PiSquaresFour } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetMusicProductsPhysicalList } from 'services/music-product-physical';
import {
  IMusicProductPhysicalResponse,
  IMusicProductPhysicalSearch,
} from 'services/music-product-physical/types/query';
import { STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { booleanSelectOptions, useEnumToSelectOptions } from 'utils/components/inputs/select';
import { getNodes } from 'utils/table/nodes.utils';

enum MUSIC_PRODUCT_PHYSICAL_COLUMNS {
  title,
  release,
  ean,
  copyrights,
  releaseStatus,
  // externalIds,
}

const musicProductPhysicalSearchInitialValues: IMusicProductPhysicalSearch = {
  ean: '',
  artistName: '',
  artistNickname: '',
  isrc: '',
  title: '',
  status: undefined,
};

const MusicProductsPhysical = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const columnsVisibility = useTableColumnsVisibility(
    'MusicProductsPhysical2',
    MUSIC_PRODUCT_PHYSICAL_COLUMNS,
  );

  const i18nextPrefix = 'music-product-physical.list';
  const viewId = 'musicProductsPhysical';

  const { searchById, setSearchById } = useStoreState();
  const filters = searchById[columnsVisibility.tableId] || musicProductPhysicalSearchInitialValues;

  const { view } = useListViewById(viewId, 'grid');

  const { data, isLoading, fetchNextPage, isFetchingNextPage } =
    useGetMusicProductsPhysicalList(filters);

  const tableData = getNodes<IMusicProductPhysicalResponse>(data?.pages.flat());

  const onSelectChange = (action: Action, state: State) => {
    navigate(`${state.id ?? state.ids[state.ids.length - 1]}`, {
      relative: 'path',
    });
  };

  const scroll = useScroll({ fetchNextPage: fetchNextPage });

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const { getSelectOptions } = useEnumToSelectOptions();

  return (
    <TopbarPage
      topbarAbsolute
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title`)}
          path=""
          customBackItem={<div />}
          topbarContent={
            <>
              <div className="flex">
                <ViewToggleButton id={viewId} />
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
              </div>
              {/* <Button
                onlyIcon
                variant={BUTTON_VARIANT.submit}
                onClick={() => navigate('create/tracks', { replace: false })}>
                <Button.Icon icon={<PiPlus />} />
              </Button> */}
            </>
          }
        >
          <QueryFilter<IMusicProductPhysicalSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}
          >
            <>
              <QueryFilter.Form<IMusicProductPhysicalSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={(values) => setSearchById(columnsVisibility.tableId, values)}
              >
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    appBackground
                    name="status"
                    label={t('input.label.status')}
                    options={getSelectOptions(STATUS, 'enum.STATUS')}
                  />
                  <FormInput.Text appBackground name="title" label={t('input.label.title')} />
                  <FormInput.Text
                    appBackground
                    name="artistNickname"
                    label={t('input.label.artistNickname')}
                  />
                  <FormInput.Text
                    appBackground
                    name="artistName"
                    label={t('input.label.artistName')}
                  />
                  <FormInput.Text
                    appBackground
                    name="ean"
                    label={t('input.label.ean')}
                    placeholder="0000000000"
                  />
                  <FormInput.Text
                    appBackground
                    name="isrc"
                    label="ISRC"
                    placeholder="USP031900001"
                  />
                  <FormInput.Select
                    appBackground
                    name="hasDuplicatedArtist"
                    label={t('input.label.hasDuplicatedArtist')}
                    options={booleanSelectOptions}
                  />
                  <FormInput.Select
                    appBackground
                    name="hasContracts"
                    label={t('input.label.hasContracts')}
                    options={booleanSelectOptions}
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilterLabels scope="musicProductsPhysical" isFormOpen={isComponentVisible} />
            </>
          </QueryFilter>
        </TopBar>
      }
    >
      <ListWrapper
        view={view}
        items={data?.pages.flat()}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        item={(data) => (
          <Card
            view={view}
            onClick={() => navigate(`/music-product-physical/${data.id}/data`)}
            image={data.productImagePreviewLink ?? ''}
            header={data.mainTitle?.title ?? ''}
            description={data.mainTitle?.version ?? ''}
            status={<LabeledStatus status={data.releaseStatus} noname />}
          />
        )}
      />
      {/* <Table
        id={columnsVisibility.tableId}
        select={select}
        columns={[
          {
            label: `${t(`${i18nextPrefix}.table.title`)}`,
            renderCell: ({ productImagePreviewLink, mainTitle, id }) => (
              <TableItem>
                <div className='flex flex-row items-center gap-4'>
                  <TableItem.Image
                    url={productImagePreviewLink}
                    alt={mainTitle.title}
                  />

                  <div className='flex flex-col'>
                    <TableItem.Text text={mainTitle.title} />
                    <TableItem.Text text={mainTitle.version} isSupplementary />
                  </div>
                </div>
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(MUSIC_PRODUCT_PHYSICAL_COLUMNS.title),
          },
          {
            label: `${t(`${i18nextPrefix}.table.release`)}`,
            renderCell: ({ releaseType, releaseDate }) => (
              <TableItem>
                <TableItem.Text
                  text={
                    releaseType
                      ? t(`enum.RELEASE_TYPE.${RELEASE_TYPE[releaseType]}`)
                      : undefined
                  }
                />
                <TableItem.Text
                  text={releaseDate ? formatDate(releaseDate) : undefined}
                  isSupplementary
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              MUSIC_PRODUCT_PHYSICAL_COLUMNS.release
            ),
          },
          {
            label: `${t(`${i18nextPrefix}.table.ean`)}`,
            renderCell: ({ ean }) => (
              <TableItem>
                <TableItem.Text text={ean ?? undefined} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(MUSIC_PRODUCT_PHYSICAL_COLUMNS.ean),
          },
          {
            label: t(`${i18nextPrefix}.table.copyrights`),
            renderCell: ({ cLine, pLine }) => (
              <TableItem>
                <TableItem.Text
                  isSupplementary
                  text={
                    cLine?.name && cLine?.year
                      ? `© ${cLine.year} ${cLine.name}`
                      : undefined
                  }
                />
                <TableItem.Text
                  isSupplementary
                  text={
                    pLine?.name && pLine?.year
                      ? `℗ ${pLine.year} ${pLine.name}`
                      : undefined
                  }
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              MUSIC_PRODUCT_PHYSICAL_COLUMNS.copyrights
            ),
          },
          {
            label: t(`${i18nextPrefix}.table.releaseStatus`),
            renderCell: ({ releaseStatus }) => (
              <TableItem justifyPosition='center'>
                <LabeledStatus status={releaseStatus} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              MUSIC_PRODUCT_PHYSICAL_COLUMNS.releaseStatus
            ),
          },
        ]}
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        data={tableData}
        displayMode
      /> */}
    </TopbarPage>
  );
};

export default MusicProductsPhysical;
